/**
 * Тонкая обёртка над window.Telegram.WebApp.
 *
 * Каждый вызов обёрнут в try/catch: набор поддерживаемых методов зависит
 * от версии клиента, и на старых Telegram вызов падает с
 * WebAppMethodUnsupported. Уронить из-за этого бизнес-сценарий нельзя —
 * при отказе просто откатываемся на браузерный аналог или тихо пропускаем.
 */

interface HapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
}

interface MainButton {
  text: string;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  setText(text: string): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface BackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    start_param?: string;
    user?: { id: number; language_code?: string };
  };
  /** ios | android | tdesktop | web | macos… */
  platform?: string;
  colorScheme: 'light' | 'dark';
  version: string;
  themeParams: Record<string, string>;
  MainButton: MainButton;
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  /** Bot API 8.0+. На старых клиентах отсутствуют. */
  requestSafeArea?(): void;
  requestContentSafeArea?(): void;
  requestFullscreen?(): void;
  /** Bot API 7.7+: запрет свайпа вниз, который сворачивает окно. */
  disableVerticalSwipes?(): void;
  isVerticalSwipesEnabled?: boolean;
  close(): void;
  /** Bot API 6.1+: подтверждение при закрытии свайпом. */
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  openTelegramLink(url: string): void;
  showAlert(message: string, cb?: () => void): void;
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  /** Подписка на события клиента: themeChanged и прочие. */
  onEvent?(event: string, handler: () => void): void;
  offEvent?(event: string, handler: () => void): void;
  /**
   * Bot API 8.0+: геопозиция мини-приложения. На новых клиентах
   * `navigator.geolocation` в вебвью Mini App больше не работает
   * (`PERMISSION_DENIED` без запроса) — это единственный рабочий путь.
   */
  LocationManager?: TgLocationManager;
}

export interface TgLocationData {
  latitude: number;
  longitude: number;
  altitude: number | null;
  course: number | null;
  speed: number | null;
  horizontal_accuracy: number | null;
  vertical_accuracy: number | null;
  course_accuracy: number | null;
  speed_accuracy: number | null;
}

interface TgLocationManager {
  isInited: boolean;
  /** Платформа умеет отдавать геопозицию. Осмысленно только после init. */
  isLocationAvailable: boolean;
  /** Разрешение хоть раз запрашивалось (был ответ пользователя). */
  isAccessRequested: boolean;
  /** Доступ выдан. */
  isAccessGranted: boolean;
  init(callback?: () => void): void;
  getLocation(callback: (data: TgLocationData | null) => void): void;
  /** Открыть настройки доступа мини-приложения к геопозиции в Telegram. */
  openSettings(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export const tg = window.Telegram?.WebApp;

/** Приложение открыли внутри Telegram, а не в обычной вкладке. */
export const isTelegram = Boolean(tg?.initData);

/** Вызов метода Telegram, который может быть не реализован в этой версии. */
function attempt(fn: () => void): boolean {
  if (!tg) return false;
  try {
    fn();
    return true;
  } catch {
    return false;
  }
}

export function initTelegram() {
  // Метка для стилей: внутри Telegram сверху плавают «Закрыть», свернуть
  // и «…», и содержимому нужен запас, которого в браузере не нужно.
  if (isTelegram) {
    document.documentElement.dataset.tma = '1';

    // На телефоне «Закрыть» и «…» плавают поверх содержимого, на компьютере
    // они в рамке окна — и запас под них там только мешает.
    const mobile = ['ios', 'android'].includes(tg?.platform ?? '');
    document.documentElement.dataset.tmaMobile = mobile ? '1' : '0';
  }

  attempt(() => tg!.ready());
  attempt(() => tg!.expand());

  // Просим Telegram сообщать безопасные отступы. Метод появился в Bot API 8.0;
  // на старых клиентах вызов просто не пройдёт, и разметка возьмёт env().
  attempt(() => tg!.requestContentSafeArea?.());
  attempt(() => tg!.requestSafeArea?.());
  attempt(() => tg!.requestContentSafeArea?.());

  // Свайп вниз по умолчанию сворачивает Mini App: при прокрутке ленты
  // приложение уезжает в трей вслед за пальцем. Запрещаем — прокрутка
  // должна оставаться прокруткой.
  attempt(() => tg!.disableVerticalSwipes?.());

  // Полноэкранный режим намеренно не просим: в окне на десктопе он
  // растягивает приложение во весь экран, а сверху остаётся пустая полоса.
  // Обычного expand() достаточно.

  // Тактильный отклик на нажатия по всему приложению — один делегат.
  initHaptics();
}

/*
 * Последний тактильный отклик — чтобы два подряд не слились в дребезг.
 *
 * Слабый импакт шлёт и глобальный делегат по нажатию (см. `initHaptics`),
 * и явные вызовы `haptic()` в обработчиках. По одному месту это один и тот
 * же «щелчок» с разницей в пару миллисекунд — гасим второй.
 */
let lastImpact = 0;

export function haptic(type: 'light' | 'medium' | 'heavy' = 'light') {
  const now = Date.now();
  if (type === 'light' && now - lastImpact < 60) return;
  lastImpact = now;
  attempt(() => tg!.HapticFeedback.impactOccurred(type));
}

export function notify(type: 'error' | 'success' | 'warning') {
  lastImpact = Date.now();
  attempt(() => tg!.HapticFeedback.notificationOccurred(type));
}

/**
 * Тактильный отклик на любое нажатие по управляющему элементу.
 *
 * «Расставить haptic() по всем кнопкам» вручную — это десятки правок и
 * вечный источник пропусков. Один делегат на документе закрывает весь ТМА
 * разом: слабый щелчок на нажатие по кнопке, ссылке, чипу, переключателю.
 * Явные вызовы в коде остаются — они дают отклик посильнее в значимых
 * местах (успех отправки, ошибка) поверх базового.
 *
 * Только в Telegram: в браузере вибрации нет, а `navigator.vibrate` в
 * вебе на кнопках раздражает.
 */
export function initHaptics() {
  if (!isTelegram) return;

  const SELECTOR =
    'button, a[href], summary, [role="button"], [role="tab"], ' +
    'input[type="checkbox"], input[type="radio"], .chip, .seg__btn';

  document.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>(SELECTOR);
      if (!el) return;
      // Явный отказ и заблокированные элементы — без отклика.
      if (el.hasAttribute('data-nohaptic') || el.getAttribute('aria-disabled') === 'true') return;
      if ((el as HTMLButtonElement).disabled) return;
      haptic('light');
    },
    { passive: true, capture: true },
  );
}

/**
 * Ссылка на Telegram. Внутри Mini App открывается родным методом —
 * обычный переход выкинул бы человека в браузер поверх приложения.
 */
export function openTelegram(url: string) {
  if (attempt(() => tg!.openTelegramLink(url))) return;
  window.open(url, '_blank', 'noopener');
}

export function alert(message: string) {
  if (attempt(() => tg!.showAlert(message))) return;
  window.alert(message);
}

export function confirm(message: string): Promise<boolean> {
  if (!tg) return Promise.resolve(window.confirm(message));

  return new Promise((resolve) => {
    try {
      tg.showConfirm(message, resolve);
    } catch {
      resolve(window.confirm(message));
    }
  });
}

/**
 * Системная кнопка «назад» — стек обработчиков.
 *
 * Одна кнопка на всё приложение, а закрываемых слоёв несколько: карточка
 * заявки, лист «Ещё», панель уведомлений, пикеры. Каждый открытый слой
 * кладёт свой обработчик на стек; кнопка видна, пока стек не пуст, и
 * закрывает верхний слой — это и есть «Назад», когда есть куда.
 *
 * Опустел стек — кнопка скрывается: выход из приложения остаётся на
 * системном жесте и «крестике» Telegram, навязывать «закрыть» кнопкой
 * «назад» неправильно, это другое действие.
 */
let backStack: Array<{ id: number; fn: () => void }> = [];
let backSeq = 1;
let backWired: (() => void) | null = null;

function syncBackButton() {
  if (!tg?.BackButton) return;
  const top = backStack[backStack.length - 1];
  try {
    if (backWired) tg.BackButton.offClick(backWired);
    backWired = null;
    if (top) {
      backWired = () => top.fn();
      tg.BackButton.onClick(backWired);
      tg.BackButton.show();
    } else {
      tg.BackButton.hide();
    }
  } catch {
    // Старый клиент без BackButton — молча: своя навигация на месте.
  }
}

/** Положить обработчик «назад» на стек. Возвращает функцию снятия. */
export function pushBackHandler(fn: () => void): () => void {
  const entry = { id: backSeq++, fn };
  backStack.push(entry);
  syncBackButton();
  return () => {
    backStack = backStack.filter((e) => e.id !== entry.id);
    syncBackButton();
  };
}

/** Закрыть Mini App. */
export function closeApp() {
  attempt(() => tg!.close());
}

/**
 * Подтверждение выхода.
 *
 * Включается там, где закрытие сотрёт работу: наполовину заполненная форма
 * публикации, начатый отклик. На просмотре ленты подтверждение только
 * раздражает, поэтому включаем и выключаем адресно.
 */
export function closingConfirmation(on: boolean) {
  attempt(() =>
    on ? tg!.enableClosingConfirmation?.() : tg!.disableClosingConfirmation?.(),
  );
}

/** Браузерная геолокация — запасной путь вне Telegram и на старых клиентах. */
function browserLocation(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Браузер не поддерживает определение геопозиции'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? 'Доступ к геопозиции запрещён — разрешите в настройках'
              : 'Не удалось определить местоположение',
          ),
        );
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  });
}

/**
 * Разовая геопозиция — единая точка входа для всех карт.
 *
 * В новых клиентах Telegram `navigator.geolocation` внутри Mini App
 * возвращает `PERMISSION_DENIED` без запроса — вебвью не делегирует
 * разрешение. Единственный рабочий путь там — `WebApp.LocationManager`
 * (Bot API 8.0): `init` → `getLocation`, разрешение спрашивает сам
 * Telegram. Вне Telegram и на клиентах постарше — браузерная геолокация.
 *
 * `telegram-web-app.js` подключён и в веб-версии, поэтому `LocationManager`
 * как объект существует и в обычном браузере — но там `init` уходит в
 * никуда. Гейт по `isTelegram` (наличие `initData`), а не по наличию
 * объекта; плюс страховочный таймаут, если `init`/`getLocation` молчат.
 */
export function requestLocation(): Promise<{ lat: number; lng: number }> {
  const lm = tg?.LocationManager;
  if (!isTelegram || !lm) return browserLocation();

  return new Promise((resolve, reject) => {
    let done = false;
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      clearTimeout(guard);
      fn();
    };
    // Клиент заявил LocationManager, но не отвечает — не висим, уходим
    // на браузерную геолокацию.
    const guard = setTimeout(() => {
      finish(() => browserLocation().then(resolve, reject));
    }, 8000);

    const pull = () => {
      if (done) return;
      if (!lm.isLocationAvailable) {
        // Платформа не поддерживает — вдруг вебвью всё же отдаст.
        finish(() => browserLocation().then(resolve, reject));
        return;
      }
      lm.getLocation((data) => {
        if (data) {
          finish(() => resolve({ lat: data.latitude, lng: data.longitude }));
          return;
        }
        // null — доступ не выдан. Уже отказывали — ведём в настройки Telegram.
        if (lm.isAccessRequested && !lm.isAccessGranted) {
          try {
            lm.openSettings();
          } catch {
            /* метода нет — просто сообщаем */
          }
          finish(() =>
            reject(
              new Error(
                'Доступ к геопозиции для мини-приложения выключен. Включите его в настройках Telegram.',
              ),
            ),
          );
        } else {
          finish(() => reject(new Error('Не удалось определить местоположение')));
        }
      });
    };

    try {
      if (lm.isInited) pull();
      else lm.init(pull);
    } catch {
      finish(() => browserLocation().then(resolve, reject));
    }
  });
}

/**
 * Разбор deep link вида ?startapp=cargo_abc123.
 * Бот шлёт такие ссылки в пушах, чтобы открывать нужный экран сразу.
 */
export function parseStartParam(): { screen: string; id?: string } | null {
  let raw: string | null = null;
  try {
    raw = tg?.initDataUnsafe.start_param ?? null;
  } catch {
    raw = null;
  }
  raw ??= new URLSearchParams(window.location.search).get('startapp');
  if (!raw) return null;

  const [screen, id] = raw.split('_');
  return screen ? { screen, id } : null;
}


/**
 * Копирование в буфер.
 *
 * В webview Telegram `navigator.clipboard` доступен не везде, поэтому
 * при отказе подставляем скрытое поле и копируем старым способом —
 * иначе код приглашения придётся переписывать с экрана руками.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    haptic('light');
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}
